// Component: Q&A Pair Form - Create and Edit

import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  message,
  Spin,
  Row,
  Col,
  Tag
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { qaPairService } from '../../../services/qaPairService';
import type { QAPair, QAPairCreateData, QAPairUpdateData } from '../../../types/qaPair';

const { TextArea } = Input;
const { Option } = Select;

const QAPairForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [qaPair, setQaPair] = useState<QAPair | null>(null);

  const isEditMode = Boolean(id);

  // Load data for edit mode
  useEffect(() => {
    if (isEditMode && id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await qaPairService.getById(parseInt(id, 10));
      const data = response.data;
      setQaPair(data);
      
      // Fill form
      form.setFieldsValue({
        question: data.Question,
        answer: data.Answer,
        category: data.Category,
        tags: data.Tags ? data.Tags.split(', ') : [],
        relevantArticles: data.RelevantArticles || '',
        status: data.Status
      });
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Không thể tải dữ liệu');
      navigate('/admin/virtual-advisor/qa-pairs');
    } finally {
      setLoading(false);
    }
  };

  // Handle submit
  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      if (isEditMode && id) {
        // Update
        const updateData: QAPairUpdateData = {
          question: values.question,
          answer: values.answer,
          category: values.category,
          tags: values.tags,
          relevantArticles: values.relevantArticles,
          status: values.status
        };
        
        await qaPairService.update(parseInt(id, 10), updateData);
        message.success('Cập nhật Q&A pair thành công');
      } else {
        // Create
        const createData: QAPairCreateData = {
          question: values.question,
          answer: values.answer,
          answerSource: 'manual',
          category: values.category,
          tags: values.tags,
          relevantArticles: values.relevantArticles
        };
        
        await qaPairService.create(createData);
        message.success('Tạo Q&A pair thành công');
      }
      
      navigate('/admin/virtual-advisor/qa-pairs');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Không thể lưu Q&A pair');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/virtual-advisor/qa-pairs')}
          />
          {isEditMode ? 'Chỉnh sửa Q&A Pair' : 'Tạo Q&A Pair mới'}
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: 'active',
          category: undefined,
          tags: []
        }}
      >
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="question"
              label="Câu hỏi"
              rules={[
                { required: true, message: 'Vui lòng nhập câu hỏi' },
                { min: 10, message: 'Câu hỏi phải có ít nhất 10 ký tự' },
                { max: 1000, message: 'Câu hỏi không được vượt quá 1000 ký tự' }
              ]}
            >
              <TextArea
                rows={3}
                placeholder="Nhập câu hỏi mà học sinh/phụ huynh thường hỏi..."
                showCount
                maxLength={1000}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="answer"
              label="Câu trả lời"
              rules={[
                { required: true, message: 'Vui lòng nhập câu trả lời' },
                { min: 20, message: 'Câu trả lời phải có ít nhất 20 ký tự' },
                { max: 5000, message: 'Câu trả lời không được vượt quá 5000 ký tự' }
              ]}
            >
              <TextArea
                rows={6}
                placeholder="Nhập câu trả lời chi tiết, chính xác..."
                showCount
                maxLength={5000}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="category"
              label="Danh mục"
              rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}
            >
              <Select placeholder="Chọn danh mục">
                <Option value="Quy chế">Quy chế</Option>
                <Option value="Học phí">Học phí</Option>
                <Option value="Lịch học">Lịch học</Option>
                <Option value="Điểm số">Điểm số</Option>
                <Option value="Chính sách">Chính sách</Option>
                <Option value="Thủ tục">Thủ tục</Option>
                <Option value="Hoạt động ngoại khóa">Hoạt động ngoại khóa</Option>
                <Option value="Khác">Khác</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="status"
              label="Trạng thái"
              rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
            >
              <Select placeholder="Chọn trạng thái">
                <Option value="active">Đang dùng</Option>
                <Option value="inactive">Tạm ngưng</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="tags"
              label="Tags"
              help="Các từ khóa giúp tìm kiếm nhanh hơn"
            >
              <Select
                mode="tags"
                placeholder="Nhập các tag và nhấn Enter"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="relevantArticles"
              label="Bài viết liên quan"
              help="Tiêu đề hoặc link tới các bài viết/tài liệu tham khảo"
            >
              <TextArea
                rows={2}
                placeholder="VD: Quy chế học vụ 2024, Thông báo số 123/TB-TH..."
                maxLength={500}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Info box for edit mode */}
        {isEditMode && qaPair && (
          <Card
            type="inner"
            title="Thông tin thống kê"
            style={{ marginBottom: 24, backgroundColor: '#f5f5f5' }}
            size="small"
          >
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <div>
                  <strong>Trạng thái duyệt:</strong>
                  <div style={{ marginTop: 8 }}>
                    {qaPair.ReviewStatus === 'approved' && <Tag color="green">Đã duyệt</Tag>}
                    {qaPair.ReviewStatus === 'pending' && <Tag color="orange">Chờ duyệt</Tag>}
                    {qaPair.ReviewStatus === 'rejected' && <Tag color="red">Từ chối</Tag>}
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div>
                  <strong>Lượt sử dụng:</strong> {qaPair.UseCount}
                </div>
                <div style={{ marginTop: 4 }}>
                  <strong>Tỷ lệ thành công:</strong> {' '}
                  {qaPair.SuccessRate ? `${(qaPair.SuccessRate * 100).toFixed(1)}%` : 'N/A'}
                </div>
              </Col>
              <Col span={8}>
                <div>
                  <strong>Nguồn:</strong> {' '}
                  {qaPair.AnswerSource === 'manual' && 'Nhập tay'}
                  {qaPair.AnswerSource === 'chat_history' && 'Chat history'}
                  {qaPair.AnswerSource === 'generated' && 'AI tạo'}
                </div>
              </Col>
            </Row>
          </Card>
        )}

        {/* Action buttons */}
        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={submitting}
            >
              {isEditMode ? 'Cập nhật' : 'Tạo mới'}
            </Button>
            <Button onClick={() => navigate('/admin/virtual-advisor/qa-pairs')}>
              Hủy
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default QAPairForm;
